<script setup>
import { ref, defineProps, onMounted, defineEmits, watch } from "vue";
import Price from '@/components/three-in-one/toshiba/price/v1/shoppingCartPrice.vue';
import { useTipMsgBox } from "@/hooks/common/msg-box/useTipMsgBox";
import { useProductAttributes, useHandleAttributes } from '@/hooks/common/useProductAttributes';
import { debounce } from "lodash-es";
import { _CSR_IS_B2b } from "@/const/shopifyConst.js";
import { useGetStorage } from '@/hooks/common/useStorage';
import FeatureIcon from "./FeatureIcon.vue";
import { isPhonePortrait } from '@/hooks/common/useUtils';
import Energylabel from "@/components/three-in-one/toshiba/product/product-item/v1/Energylabel.vue";

const prop = defineProps({
    stateKey: {
        type: String,
        default: ''
    },
    product: {
        type: Object,
        default: null
    },
    isInvalidProduct: {
      type: Boolean,
      default: false
    },
    cartConfig: {
      type: Object,
      default: null
    },
    // 购物车所有商品
    cartItems: {
      type: Array,
      default: () => []
    }
});
const quantity = ref(0);
const lastValidValue = ref(1);
const productItem = ref({})
const variantsValue = ref([]);
const colorName = ref('');
const colorValue = ref('');
const sku = ref('');
const productAttributes = ref([]);
const shopifyVariant= ref(null);
const showSaveText = ref(false);
const sameProductQuantity = ref(0); // 同一个商品的当前购买数量和，针对buy x get y的Y数量计算
const bMaxiMum = ref(null); // 商品B端最大数量
const isB2b = ref(false); // 是否是B端用户
isB2b.value = useGetStorage(_CSR_IS_B2b) === true;
const cartNode = ref(null); // 购物车返回信息

onMounted(() => {
  quantity.value = prop.product?.quantity;
  productItem.value = prop.product?.product?.product;
  showSaveText.value = prop.product?.product?.product?.showSaveText; 
  shopifyVariant.value= prop.product?.variant;
  console.log('productItem.value: ', productItem.value);
  const variantsValues = prop.product?.product?.variantsValue?.split(':');
  const variantsType = prop.product?.product?.variantsType?.split(':');
  const colorIndex = variantsType?.findIndex(item => item === 'Color');
  if (colorIndex > -1) {
    colorName.value = prop.product?.product?.variantsName?.split(':')[colorIndex];
    colorValue.value = variantsValues?.splice(colorIndex, 1);
  }
  variantsValue.value = variantsValues;
  sku.value = prop.product.sku;
  const link = productItem.value?.link;
  productAttributes.value = [{
    key: '_productImage',
    value: productItem.value?.mainImage || (productItem.value?.image && productItem.value?.image[0]),
  }, {
    key: '_productLink',
    value: link?.indexOf('?wcmmode') > -1 ? location.origin + link.split('?wcmmode')[0] : location.origin + link
  }]
})

watch(() => prop.product?.quantity, (newVal) => {
  quantity.value = newVal;
}, { immediate: true });

watch(() => prop.cartItems, (newVal) => {
  // 因为buy x get y折扣，shopify会根据数量把同一件商品拆成两项，所以要实时更新商品的数量和同一件商品已购买的数量
  const curProductMerchandiseId = prop.product.variant.id;
  const curProductQuantity = newVal.reduce((cur, item) => {
    if (item.variant.id === curProductMerchandiseId) {
      cur = cur + item.quantity
    }
    return cur
  }, 0);
  sameProductQuantity.value = curProductQuantity;
  quantity.value = prop.product.quantity;
}, { immediate: true });

watch(() => prop.product, (newVal) => {
  cartNode.value = newVal.node;
}, { immediate: true });

const validateQuantity = (value) => {
  const num = parseFloat(value);
  return !isNaN(num) && num > 0;
}

const handleChangeQuantity = (event) => {
  const value = event.target.value;
  if (validateQuantity(value)) {
    if (value >= prop.product?.quantityAvailable) {
      quantity.value = prop.product?.quantityAvailable;
    } else {
      quantity.value = Number(value);
    }
    lastValidValue.value = quantity.value;
    debounceUpdataline();
  } else {
    quantity.value = 1;
    debounceUpdataline();
  }
}

const debounceUpdataline = debounce(() => {
  handleUpdateLineItem('updateLineItem');
}, 500);


const $emitEvents = defineEmits(["delLineItems", "minusLineItem", "addLineItem", "updateLineItem", "updateBProductQuntity"]);

const handleDelLineItems = () => {
  useTipMsgBox({message: prop?.cartConfig?.deleteProductPrompt, confirmButtonText: prop?.cartConfig?.deleteProductConfirmYesPrompt, cancelButtonText: prop?.cartConfig?.deleteProductConfirmNoPrompt,style: "csr-messagebox--cart"  }, () => {
    	$emitEvents("delLineItems", {
        id: prop.product.id,
        // sku: prop.product.sku,
        isInvalidProduct: prop.isInvalidProduct
      });
  });

}

const handleMinusLineItem = () => {
  if (quantity.value === 1){
    return;
  } else {
    quantity.value = quantity.value - 1;
    handleUpdateLineItem('minusLineItem')
  }
}

const handleAddLineItem = () => {
  if (quantity.value >= prop.product.quantityAvailable){
    return;
  } else {
    quantity.value = quantity.value + 1;
    handleUpdateLineItem('addLineItem');
  }
}

const handleUpdateLineItem = (eventType) => {
  const { variantsName, variantsType, variantsTitle } = prop?.product?.product || {};
    const attributes = useProductAttributes({ variantsName, variantsType, variantsTitle });
    const finalAttributes = useHandleAttributes(productAttributes.value.concat(attributes));
    $emitEvents(eventType, {
      id: prop.product.id,
      quantity: quantity.value,
      attributes: finalAttributes,
    });
}

const  handleClickItem = () => {
  const viewAsPublishUrlMark = 'wcmmode=disabled';
  if (location.href.includes(viewAsPublishUrlMark)) {
    window.location.href = `${productItem.value.link}?${viewAsPublishUrlMark}`;
  } else {
    window.location.href = productItem.value.link;
  }
}

const handlePriceLoaded = (variants) => {
  // 如果商品是B端商品，并且有最大数量限制，则需要判断数量是否超过最大数量
  bMaxiMum.value = variants?.quantityRule?.maximum;
  if (isB2b.value && bMaxiMum.value) {
    $emitEvents("updateBProductQuntity", {
      [sku.value]: {
        isExceedMaximum: bMaxiMum.value < quantity.value
      }
    })
  }
}

watch(() => quantity.value, (newVal) => {
  if (isB2b.value && bMaxiMum.value) {
    $emitEvents("updateBProductQuntity", {
      [sku.value]: {
        isExceedMaximum: bMaxiMum.value < newVal
      }
    })
  }
})
</script>

<template>
  <div class="shopping-cart-item">
    <div class="shopping-cart-item__image">
      <img v-if="productItem?.mainImage" :src="productItem?.mainImage" :alt="productItem?.altText || productItem?.productName" />
      <img v-else :src="productItem?.firstImage" :alt="productItem?.firstImageAltText" />
    </div>
    <div class="shopping-cart-item__detail">
      <div class="shopping-cart-item__name" @click="handleClickItem">
        {{ productItem?.productName }}
      </div>
      <div class="shopping-cart-item__params">
        <span class="shopping-cart-item__model">{{ productItem?.productModelNumber }}</span>
        <div class="shopping-cart-item__product-params" v-if="cartConfig?.displayVariant === '1'">
          <div class="shopping-cart-item__params-item" v-if="colorValue">
            {{ colorName }}
          </div>
          <div class="shopping-cart-item__params-item" v-for="(variants, index) in variantsValue" :key="'varints' + index">{{ variants }}</div>
        </div>
      </div>
      <FeatureIcon 
        v-if="cartConfig?.displayFeatureIcon === '1' && !isPhonePortrait()"
        :config="cartConfig"
        :featureIcon="productItem?.featureIcon"
      />
      <Energylabel :activeSkuProduct="productItem" />
      <div class="shopping-cart-item__price">
        <Price
          v-if="shopifyVariant"
          :node="cartNode"
          :dataset="{'saveTo': cartConfig?.saveText ,'showSaveText':showSaveText}"
          @priceLoaded="handlePriceLoaded"
        />
      </div>
      <!--如果是移动端，数量（或Out of Stock）和删除按钮，需要移动到下面展示-->
      <!-- v-if="!isPhonePortrait()" -->
      <div class="shopping-cart-item__bottom">
        <div class="shopping-cart-item__num-input flex" v-if="!isInvalidProduct">
          <button class="shopping-cart-item__num-del" :disabled="quantity === 1 || productItem?.archive">
            <svg :class="[(quantity === 1 || productItem?.archive) && 'disabled', 'shopping-cart-item__num-icon']" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" fill="none" version="1.1" width="24" height="24" viewBox="0 0 24 24" @click="handleMinusLineItem"><defs><clipPath id="master_svg0_1528_38429/676_35430/676_34670"><rect x="0" y="0" width="24" height="24" rx="0" /></clipPath></defs><g clip-path="url(#master_svg0_1528_38429/676_35430/676_34670)"><g><path d="M5.25,13L19.25,13C19.802300000000002,13,20.25,12.55228,20.25,12C20.25,11.447715,19.802300000000002,11,19.25,11L5.25,11C4.697715,11,4.25,11.447715,4.25,12C4.25,12.55228,4.697715,13,5.25,13Z" fill-rule="evenodd" fill="#333333" fill-opacity="1" /></g></g></svg>
          </button>
          <input name="quantity" type="number" class="shopping-cart-item__num" :class="{'is-archive': productItem?.archive}" v-model="quantity" min="1" @input="handleChangeQuantity" />
          <button class="shopping-cart-item__num-add" :disabled="quantity >= product.quantityAvailable || productItem?.archive">
            <svg 
              :class="[(quantity >= product.quantityAvailable || productItem?.archive) && 'disabled', 'shopping-cart-item__num-icon']"
              xmlns="http://www.w3.org/2000/svg" 
              xmlns:xlink="http://www.w3.org/1999/xlink"
              fill="none" 
              version="1.1"
              width="24"
              height="24" 
              viewBox="0 0 24 24"
              @click="handleAddLineItem"
            ><defs><clipPath id="master_svg0_1528_38429/676_35431/676_35437"><rect x="0" y="0" width="24" height="24" rx="0" /></clipPath></defs><g clip-path="url(#master_svg0_1528_38429/676_35431/676_35437)"><g><path d="M11.011719584465,18.9987L11.03001795,5L11.03001965,4.998693C11.03064985,4.516587,11.37516875,4.103563,11.84934075,4.0164576C11.90894375,4.00550854,11.969418749999999,4,12.03001875,4L12.03132875,4.000000834465C12.51342875,4.000631034,12.92645875,4.34515,13.01355875,4.819322C13.024508749999999,4.878925,13.03001875,4.9394,13.03001875,5L13.03001875,5.00131L13.01171875,19L13.01171875,19.0013C13.011368749999999,19.2665,12.90567875,19.520699999999998,12.71789875,19.708C12.54187875,19.8836,12.30677875,19.987299999999998,12.05844875,19.9989C12.04287875,19.9996,12.02729875,20,12.01171875,20L12.01041175,20C11.52830575,19.9994,11.11528175,19.6548,11.02817635,19.1807C11.01722729,19.1211,11.01171875,19.0606,11.01171875,19L11.011719584465,18.9987Z" fill-rule="evenodd" fill="#707070" fill-opacity="1" /></g><g><path d="M5,13L19,13C19.552300000000002,13,20,12.55228,20,12C20,11.447715,19.552300000000002,11,19,11L5,11C4.447715,11,4,11.447715,4,12C4,12.55228,4.447715,13,5,13Z" fill-rule="evenodd" fill="#707070" fill-opacity="1" /></g></g></svg>
          </button>
        </div>
        <div class="shopping-cart-item__out-of-stock" v-else>{{ cartConfig?.outOfStock || 'Out of Stock' }}</div>
        <i class="delete-icon iconfont-toshiba icon_clean" @click="handleDelLineItems">
        </i>
      </div>
      <FeatureIcon 
        v-if="cartConfig?.displayFeatureIcon === '1' && isPhonePortrait()"
        :config="cartConfig"
        :featureIcon="productItem?.featureIcon"
      />
    </div>
  </div>
</template>